import { useState, useMemo } from "react";
import { Card, Input, Select, Table, Button, Typography, Badge, Col, Row, Tooltip } from "antd";
import { CheckCircleOutlined, ExclamationCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFile, faEye, faEyeSlash, faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { ExcelExportButton } from "./exportToExcel";

const { Title, Paragraph } = Typography;
const { Option } = Select;

export function FileManagement({ allFiles, projectStats, hidenMenu, ocultarData, permissionExport }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProject, setSelectedProject] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const baseUrl = "https://bodecom.com";
  const baseDir = `${baseUrl}/DocumentosFAO/`;

  const projectNames = useMemo(() => {
    return Object.keys(projectStats).sort();
  }, [projectStats]);


  const getProjectName = (fullPath) => {
    if (!fullPath) return 'No especificado';
    const pathParts = fullPath.split('/');
    if (pathParts.length >= 6) {
      return pathParts[5];
    }
    if (pathParts.length > 0) {
      const projectPart = pathParts.find(part => part.match(/^P\d+/));
      if (projectPart) {
        return projectPart;
      }
      // Si no encuentra, retornar la primera parte que no sea genérica
      const nonGenericParts = pathParts.filter(part =>
        !part.includes('actividad_') &&
        !part.includes('tarea_') &&
        !part.includes('indicador_') &&
        !part.includes('medio_verificacion_') &&
        part !== 'DocumentosPOA'
      );
      if (nonGenericParts.length > 0) {
        return nonGenericParts[0];
      }
    }
    return 'No especificado';
  };

  const getValidationStatus = (file) => {
    if (file.validado === 2) return "validated";
    if (file.validado === 1) return "notValidated";
    return "notRegistered";
  };

  const filteredFiles = useMemo(() => {
    return allFiles.filter((file) => {
      const matchesSearch =
        !searchTerm ||
        file.nameResource?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        file.user?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        file.codUnico?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        getProjectName(file.fullPath)?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesProject =
        selectedProject === "all" || file.fullPath.includes(selectedProject);
      const matchesStatus =
        selectedStatus === "all" || getValidationStatus(file) === selectedStatus;
      return matchesSearch && matchesProject && matchesStatus;
    });
  }, [allFiles, searchTerm, selectedProject, selectedStatus]);

  const getStatusBadge = (file) => {
    const status = getValidationStatus(file);
    switch (status) {
      case "validated":
        return <Badge color="green" text="Validado" icon={<CheckCircleOutlined />} />;
      case "notValidated":
        return <Badge color="orange" text="Sin Validar" icon={<ExclamationCircleOutlined />} />;
      case "notRegistered":
        return <Badge color="red" text="Sin Registrar" icon={<CloseCircleOutlined />} />;
      default:
        return <Badge color="gray" text="Desconocido" />;
    }
  };

  const fetchFileUrl = (path) => {
    const fileUrl = `${baseDir}/${path}`;
    window.open(fileUrl, '_blank');
  };

  const columns = [
    {
      title: "Archivo",
      dataIndex: "nameResource",
      key: "nameResource",
      width: 120,
      fixed: 'left',
      render: (text, file) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 280 }}>
          <FontAwesomeIcon icon={faFile} color="#60B5EF" />
          <span style={{
            display: 'inline-block',
            maxWidth: 250,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {text}
          </span>
        </div>
      ),
    },
    {
      title: "Proyecto",
      dataIndex: "fullPath",
      key: "proyecto",
      width: 120,
      align: "center",
      render: (fullPath) => (
        <Tooltip title={getProjectName(fullPath)}>
          <span >
            {getProjectName(fullPath)}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Estado",
      dataIndex: "estado",
      align: "center",
      key: "estado",
      width: 70,
      render: (_, file) => getStatusBadge(file),
    },
    {
      title: "Código",
      dataIndex: "codUnico",
      align: "center",
      key: "codUnico",
      width: 70,
      render: (code) => (
        <span style={{ fontFamily: "monospace", fontWeight: 500 }}>
          {code || "-"}
        </span>
      ),
    },
    {
      title: "Subido por",
      dataIndex: "user",
      align: "center",
      key: "user",
      width: 70,
      render: (user) => user || "-",
    },
    {
      title: "Validado por",
      dataIndex: "validado_user",
      align: "center",
      key: "validado_user",
      width: 70,
    },
    {
      title: "Fecha Validación",
      dataIndex: "fecha_validacion",
      align: "center",
      key: "fecha_validacion",
       width: 70,
      render: (date) => (date ? new Date(date).toLocaleDateString() : "-"),
    },
    {
      title: "Acciones",
      key: "acciones",
      align: "center",
      width: 40,
      fixed: 'right',
      render: (_, record) => (
        <Tooltip title="Visualizar el archivo">
          <Button
            onClick={() => fetchFileUrl(record.fullPath)}
            style={{
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              padding: '4px 8px'
            }}
          >
            <FontAwesomeIcon icon={faEye} style={{ fontSize: '18px', color: '#08c' }} />
          </Button>
        </Tooltip>
      ),
    }
  ];

  return (
    <div style={{ padding: 20 }}>
      <Card
        style={{
          padding: 10,
          borderRadius: 5,
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          backgroundColor: "#042956",
          marginBottom: 20,
        }}
      >
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={2} style={{ margin: 0, color: "#ffffff" }}>
              <FontAwesomeIcon
                onClick={hidenMenu}
                icon={ocultarData ? faEyeSlash : faEye}
                style={{ marginRight: 10, cursor: 'pointer' }}
              />
              <FontAwesomeIcon icon={faFile} style={{ marginRight: 10 }} />
              Gestión de Archivos
            </Title>
            <Paragraph style={{ margin: 0, color: "rgba(255,255,255,0.8)" }}>
              Administrar y editar información de archivos
            </Paragraph>
          </Col>
          <ExcelExportButton
            filteredFiles={filteredFiles}
            permissionExport={permissionExport}
          />
          <Col>
            <div style={{
              flex: "0 0 auto",
              color: "#ffffff",
              fontSize: "14px",
              fontWeight: 500,
              padding: "4px 16px",
              backgroundColor: "rgba(255, 255, 255, 0.1)",
              borderRadius: "4px",
              border: "1px solid rgba(255, 255, 255, 0.2)"
            }}>
              {filteredFiles.length} archivos encontrados
            </div>
          </Col>
        </Row>
      </Card>

      <Card
        style={{
          padding: "10px",
          borderRadius: "8px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          backgroundColor: "#042956",
          marginBottom: "24px",
          border: "none",
          width: "100%"
        }}
      >
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          flexWrap: "wrap",
          gap: "16px"
        }}>
          <Title level={2} style={{
            margin: 0,
            color: "#ffffff",
            fontSize: "20px",
            fontWeight: 600
          }}>
            <FontAwesomeIcon icon={faMagnifyingGlass} style={{ marginRight: "12px", opacity: 0.9 }} />
            Filtro de búsqueda
          </Title>
        </div>

        <div style={{
          display: "flex",
          gap: "16px",
          flexWrap: "wrap",
          alignItems: "center"
        }}>
          <div style={{ flex: "1 1 200px", minWidth: "200px" }}>
            <Input
              placeholder="Buscar archivos, proyectos, códigos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: "100%",
                backgroundColor: "#ffffff",
                height: "40px"
              }}
            />
          </div>

          <div style={{ flex: "1 1 200px", minWidth: "200px" }}>
            <Select
              value={selectedProject}
              onChange={setSelectedProject}
              style={{ width: "100%", height: "40px" }}
            >
              <Option value="all">Todos los proyectos</Option>
              {projectNames.map((p) => (
                <Option key={p} value={p}>
                  {p.length > 30 ? p.substring(0, 30) + "..." : p}
                </Option>
              ))}
            </Select>
          </div>

          <div style={{ flex: "1 1 200px", minWidth: "200px" }}>
            <Select
              value={selectedStatus}
              onChange={setSelectedStatus}
              style={{ width: "100%", height: "40px" }}
            >
              <Option value="all">Todos los estados</Option>
              <Option value="validated">Validados</Option>
              <Option value="notValidated">Sin Validar</Option>
              <Option value="notRegistered">Sin Registrar</Option>
            </Select>
          </div>
        </div>
      </Card>
      <Card
        style={{
          borderRadius: "8px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          border: "none",
          width: "100%",
          overflow: "hidden"
        }}
      >
        <Table
          dataSource={filteredFiles}
          columns={columns}
          rowKey="oid"
          pagination={{
            pageSize: 50,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} de ${total} archivos`
          }}
          scroll={{
            x: 1600,
            y: 600
          }}
          size="middle"
          sticky={true}
          style={{
            fontSize: '14px'
          }}
        />
      </Card>
    </div>
  );
}